import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Table } from 'src/components/Table';
import Input from 'src/components/FormComponents/Input';
import { GroupFilterControls } from 'src/components/GroupFilterControls';
import { DocumentsIcon, PlusIcon } from 'src/components/Icons/icons';
import { SearchIcon } from 'src/components/Icons/icons';
import { MessageBubbleIcon } from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { Consumer } from 'src/domain/consumer';
import { ResidentUnreadCountBucket } from 'src/domain/message';
import {
    LicenceEntity,
    OrganizationGroup,
    OrganizationHierarchyLevel,
    OrganizationRolePermission,
} from 'src/domain/organization';
import {
    getOrganizationId,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useConsumers } from 'src/view/hooks';
import { useOrganizationGroupsById } from 'src/view/hooks/useOrganizationGroupsById';
import { useAvatarFetch } from 'src/view/hooks/useImageFetch';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useOrganizationGroupFilters } from 'src/view/hooks/useOrganizationGroupFilters';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';

import { useCurrentConsumerContext } from '../Home/NewHome/reducer';
import { ModalLimitReached } from '../MyClients/list/components/ModalLimitReached';
import { isMobile } from 'react-device-detect';
import { LoginStatusIndicator } from 'src/components/LoginStatusIndicator';
import { matchesMainContactStatus } from 'src/utils/accountStatusFilter';
import { AccountStatusFilterTabs } from 'src/components/AccountStatusFilterTabs';
import { useAccountStatusFilter } from 'src/view/hooks/useAccountStatusFilter';

const {
    cradle: { munduClient },
} = container;

const RESIDENT_UNREAD_REFRESH_INTERVAL_MS = 30 * 1000;

const getVisibleRefreshInterval = () => {
    if (typeof document === 'undefined') {
        return RESIDENT_UNREAD_REFRESH_INTERVAL_MS;
    }

    return document.visibilityState === 'visible'
        ? RESIDENT_UNREAD_REFRESH_INTERVAL_MS
        : 0;
};

const createEmptyUnreadBucket = (): ResidentUnreadCountBucket => ({
    count: 0,
    hasUnread: false,
});

type AttentionBadgeProps = {
    backgroundClassName: string;
    count?: number;
};

const AttentionBadge = ({
    backgroundClassName,
    count,
}: AttentionBadgeProps) => (
    <span
        className={`inline-flex items-center justify-center gap-1 min-w-[1.5rem] h-6 px-2 rounded-full text-woodyBrown text-xs font-semibold ${backgroundClassName}`}
    >
        <span className="relative w-3.5 h-3.5">
            <MessageBubbleIcon />
        </span>
        {typeof count === 'number' && count > 0 && <span>{count}</span>}
    </span>
);

type UsersTableRow =
    | { type: 'consumer'; consumer: Consumer }
    | { type: 'leafGroup'; group: OrganizationGroup };

type MainContactSummary = {
    id: string;
    name: string;
    lastLoggedInAt?: string;
    hasEverLoggedIn?: boolean;
};

const buildChildrenByParentId = (groups: OrganizationGroup[]) => {
    const childrenByParentId = new Map<
        string | undefined,
        OrganizationGroup[]
    >();

    for (const group of groups) {
        const parentKey = group.parentId;
        const existing = childrenByParentId.get(parentKey) ?? [];
        existing.push(group);
        childrenByParentId.set(parentKey, existing);
    }

    return childrenByParentId;
};

const buildLeafHierarchyLevelIds = (
    hierarchy: OrganizationHierarchyLevel[],
) => {
    const hasChild = new Set<string>();
    for (const level of hierarchy) {
        if (level.parentId) {
            hasChild.add(level.parentId);
        }
    }

    const leafIds = new Set<string>();
    for (const level of hierarchy) {
        if (!hasChild.has(level.id)) {
            leafIds.add(level.id);
        }
    }

    return leafIds;
};

export const Users = () => {
    const location = useLocation<
        | {
              highlightUsersWithoutNextOfKin?: boolean;
          }
        | undefined
    >();
    const { dispatch } = useCurrentConsumerContext();
    const [licence, setLicence] = useState<LicenceEntity | undefined>();
    const [isReached, setIsReached] = useState<boolean>(true);
    const [isShowModalReached, setIsShowModalReached] =
        useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const notify = useNotification();
    const { t } = useTranslation();

    const { consumers, isError, isLoading } = useConsumers();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { activeRoleGroupIds } = useCreatorRoleContext();
    const canWriteResidents = useCreatorRolePermission(
        OrganizationRolePermission.writeResidents,
    );
    const canCreate =
        !user?.supportMode &&
        (isOrganizationAdministrator(user) || canWriteResidents);
    const canFilterResidents = Boolean(user?.supportMode) || canCreate;
    const { statusFilter, setStatusFilter } =
        useAccountStatusFilter(canFilterResidents);
    const getAvatar = useAvatarFetch();
    const organizationId = useMemo(
        () => (user ? getOrganizationId(user) : undefined),
        [user],
    );
    const { data: nextOfKins } = useNextOfKins({ organizationId });
    const { groupsById } = useOrganizationGroupsById(organizationId);
    const { organization } = useCurrentOrganization();

    const organizationGroups = useMemo(
        () => organization?.groups ?? [],
        [organization?.groups],
    );
    const hierarchy = useMemo(
        () => organization?.hierarchy ?? [],
        [organization?.hierarchy],
    );
    const {
        filterLevels,
        selectedFilterCount,
        selectedFilterGroupIds: selectedResidentFilterGroupIds,
        setSelectedIdsForLevel,
    } = useOrganizationGroupFilters({
        activeRoleGroupIds,
        groups: organizationGroups,
        hierarchy,
        organizationAdmin: isOrganizationAdministrator(user, organizationId),
        role,
    });

    const { leafLevel, parentLeafLevel } = useMemo(() => {
        if (!hierarchy.length) {
            return { leafLevel: undefined, parentLeafLevel: undefined };
        }

        const byId = new Map(
            hierarchy.map(level => [level.id, level] as const),
        );
        const depthById = new Map<string, number>();

        const getDepth = (level: OrganizationHierarchyLevel): number => {
            if (depthById.has(level.id)) {
                return depthById.get(level.id) ?? 0;
            }

            if (!level.parentId) {
                depthById.set(level.id, 0);
                return 0;
            }

            const parent = byId.get(level.parentId);
            const depth = parent ? getDepth(parent) + 1 : 0;
            depthById.set(level.id, depth);
            return depth;
        };

        let maxDepth = 0;
        let leafLevelId: string | undefined;

        for (const level of hierarchy) {
            const depth = getDepth(level);
            if (depth >= maxDepth) {
                maxDepth = depth;
                leafLevelId = level.id;
            }
        }

        const leafLevel = leafLevelId ? byId.get(leafLevelId) : undefined;
        const parentLeafLevel = leafLevel?.parentId
            ? byId.get(leafLevel.parentId)
            : undefined;

        return { leafLevel, parentLeafLevel };
    }, [hierarchy]);

    const groupById = useMemo(
        () => new Map(organizationGroups.map(group => [group.id, group])),
        [organizationGroups],
    );

    const getGroupLabelForLevel = useCallback(
        (groupId: string | undefined, levelId: string | undefined): string => {
            if (!groupId || !levelId) return '';

            let current = groupById.get(groupId);

            while (current) {
                if (current.levelId === levelId) {
                    return current.name || current.id;
                }

                current = current.parentId
                    ? groupById.get(current.parentId)
                    : undefined;
            }

            return '';
        },
        [groupById],
    );

    const roleAllowedGroupIds = useMemo(() => {
        if (!activeRoleGroupIds.length) return undefined;
        const groups = organizationGroups;
        if (!groups.length) return new Set(activeRoleGroupIds);

        const childrenByParent = groups.reduce<Record<string, string[]>>(
            (acc, group) => {
                if (group.parentId) {
                    acc[group.parentId] = acc[group.parentId] || [];
                    acc[group.parentId].push(group.id);
                }
                return acc;
            },
            {},
        );

        const stack = [...activeRoleGroupIds];
        const collected = new Set<string>();

        while (stack.length) {
            const current = stack.pop();
            if (!current || collected.has(current)) continue;
            collected.add(current);
            const children = childrenByParent[current] || [];
            children.forEach(child => stack.push(child));
        }

        return collected;
    }, [activeRoleGroupIds, organizationGroups]);

    const selectedAllowedGroupIds = useMemo(() => {
        if (!selectedResidentFilterGroupIds.length) return undefined;

        const childrenByParent = organizationGroups.reduce<
            Record<string, string[]>
        >((acc, group) => {
            if (group.parentId) {
                acc[group.parentId] = acc[group.parentId] || [];
                acc[group.parentId].push(group.id);
            }
            return acc;
        }, {});

        const stack = [...selectedResidentFilterGroupIds];
        const collected = new Set<string>();

        while (stack.length) {
            const current = stack.pop();
            if (!current || collected.has(current)) continue;
            collected.add(current);
            const children = childrenByParent[current] || [];
            children.forEach(child => stack.push(child));
        }

        return collected;
    }, [organizationGroups, selectedResidentFilterGroupIds]);

    const allowedGroupIds = selectedAllowedGroupIds ?? roleAllowedGroupIds;

    const visibleGroups = useMemo(() => {
        const groups = organizationGroups;
        if (!allowedGroupIds) return groups;
        return groups.filter(group => allowedGroupIds.has(group.id));
    }, [allowedGroupIds, organizationGroups]);

    const leafHierarchyLevelIds = useMemo(
        () => buildLeafHierarchyLevelIds(hierarchy),
        [hierarchy],
    );

    const leafGroups = useMemo(() => {
        if (!hierarchy.length) return [] as OrganizationGroup[];
        if (!leafHierarchyLevelIds.size) return [] as OrganizationGroup[];

        const childrenByParentId = buildChildrenByParentId(visibleGroups);

        return visibleGroups.filter(group => {
            if (!group.levelId) return false;
            if (!leafHierarchyLevelIds.has(group.levelId)) return false;

            const hasChildren =
                (childrenByParentId.get(group.id)?.length ?? 0) > 0;
            return !hasChildren;
        });
    }, [hierarchy.length, leafHierarchyLevelIds, visibleGroups]);

    const mainContactsByConsumerId = useMemo(() => {
        const map = new Map<string, MainContactSummary[]>();

        const getFullName = (firstName?: string, lastName?: string): string =>
            `${firstName?.trim() ?? ''} ${lastName?.trim() ?? ''}`.trim();

        for (const nextOfKin of nextOfKins ?? []) {
            if (!nextOfKin.isMainContact) continue;

            const name = getFullName(
                nextOfKin.profile.firstName,
                nextOfKin.profile.lastName,
            );

            for (const consumerId of nextOfKin.followers?.consumerIds ?? []) {
                if (!consumerId) continue;

                const existing = map.get(consumerId) ?? [];
                if (existing.some(contact => contact.id === nextOfKin.id))
                    continue;
                map.set(consumerId, [
                    ...existing,
                    {
                        id: nextOfKin.id,
                        name: name || '-',
                        lastLoggedInAt: nextOfKin.lastLoggedInAt,
                        hasEverLoggedIn: nextOfKin.hasEverLoggedIn,
                    },
                ]);
            }
        }

        return map;
    }, [nextOfKins]);

    const allowedConsumers = useMemo(() => {
        if (!allowedGroupIds) return consumers;
        return consumers.filter(consumer =>
            (consumer.groupIds ?? []).some(id => allowedGroupIds.has(id)),
        );
    }, [allowedGroupIds, consumers]);

    const allowedConsumerIds = useMemo(
        () =>
            allowedConsumers
                .map(consumer => consumer.id)
                .filter((consumerId): consumerId is string =>
                    Boolean(consumerId),
                ),
        [allowedConsumers],
    );
    const allowedConsumerIdsKey = useMemo(
        () => allowedConsumerIds.join(','),
        [allowedConsumerIds],
    );
    const { data: residentUnreadCounts = {} } = useSWR(
        organizationId && allowedConsumerIds.length
            ? [
                  'residentUnreadMessageCounts',
                  organizationId,
                  allowedConsumerIdsKey,
              ]
            : null,
        () =>
            munduClient.getResidentUnreadMessageCounts({
                organizationId: organizationId as string,
                consumerIds: allowedConsumerIds,
            }),
        {
            revalidateOnFocus: true,
            refreshWhenHidden: false,
            refreshInterval: getVisibleRefreshInterval,
        },
    );

    const groupIdsWithResidents = useMemo(() => {
        const ids = new Set<string>();
        for (const consumer of allowedConsumers) {
            for (const groupId of consumer.groupIds ?? []) {
                if (groupId) ids.add(groupId);
            }
        }
        return ids;
    }, [allowedConsumers]);

    const emptyLeafGroups = useMemo(
        () => leafGroups.filter(group => !groupIdsWithResidents.has(group.id)),
        [groupIdsWithResidents, leafGroups],
    );

    const rows = useMemo<UsersTableRow[]>(() => {
        const consumerRows = allowedConsumers.map(consumer => ({
            type: 'consumer' as const,
            consumer,
        }));
        const leafRows = emptyLeafGroups.map(group => ({
            type: 'leafGroup' as const,
            group,
        }));
        return [...consumerRows, ...leafRows];
    }, [allowedConsumers, emptyLeafGroups]);

    const filteredRows = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        return rows.filter(row => {
            if (statusFilter !== 'all') {
                if (
                    row.type !== 'consumer' ||
                    !matchesMainContactStatus(
                        mainContactsByConsumerId.get(row.consumer.id) ?? [],
                        statusFilter,
                    )
                )
                    return false;
            }
            if (!query) return true;
            if (row.type === 'consumer') {
                const { consumer } = row;
                const fullName = `${consumer.profile.firstName ?? ''} ${
                    consumer.profile.lastName ?? ''
                }`.trim();
                const mainContacts =
                    mainContactsByConsumerId
                        .get(consumer.id)
                        ?.map(contact => contact.name)
                        .join(', ') ?? '';
                const groupId = consumer.groupIds?.[0];
                const groupLabel = groupId
                    ? groupsById?.get(groupId) ?? ''
                    : '';
                const parentLevelLabel = getGroupLabelForLevel(
                    groupId,
                    parentLeafLevel?.id,
                );
                const leafLevelLabel = getGroupLabelForLevel(
                    groupId,
                    leafLevel?.id,
                );

                const haystack =
                    `${fullName} ${mainContacts} ${groupLabel} ${parentLevelLabel} ${leafLevelLabel}`
                        .trim()
                        .toLowerCase();
                return haystack.includes(query);
            }

            const { group } = row;
            const groupLabel = group.name?.trim().length
                ? group.name
                : group.id;
            const parentLevelLabel = getGroupLabelForLevel(
                group.id,
                parentLeafLevel?.id,
            );
            const leafLevelLabel = getGroupLabelForLevel(
                group.id,
                leafLevel?.id,
            );

            const haystack =
                `${groupLabel} ${group.id} ${parentLevelLabel} ${leafLevelLabel}`
                    .trim()
                    .toLowerCase();
            return haystack.includes(query);
        });
    }, [
        rows,
        statusFilter,
        groupsById,
        mainContactsByConsumerId,
        searchValue,
        parentLeafLevel?.id,
        leafLevel?.id,
        getGroupLabelForLevel,
    ]);

    const consumerIdsWithNextOfKin = useMemo(() => {
        const ids = new Set<string>();
        for (const nok of nextOfKins ?? []) {
            for (const consumerId of nok.followers?.consumerIds ?? []) {
                if (consumerId) ids.add(consumerId);
            }
        }
        return ids;
    }, [nextOfKins]);

    // Support-mode drilldowns can only pass a URL, so the highlight is also accepted as a parameter.
    const shouldHighlightUsersWithoutNextOfKin =
        Boolean(location.state?.highlightUsersWithoutNextOfKin) ||
        new URLSearchParams(location.search).get('highlight') ===
            'withoutRelatives';

    const columns = useMemo(
        () => [
            {
                header: t('common.name'),
                sortKey: 'name',
                sortValue: (row: UsersTableRow) =>
                    row.type === 'consumer'
                        ? `${row.consumer.profile.firstName ?? ''} ${
                              row.consumer.profile.lastName ?? ''
                          }`.trim()
                        : '',
            },
            ...(isMobile
                ? []
                : [
                      {
                          header: parentLeafLevel?.name || t('common.group'),
                          sortKey: 'parentLeafGroup',
                          sortValue: (row: UsersTableRow) => {
                              const groupId =
                                  row.type === 'consumer'
                                      ? row.consumer.groupIds?.[0]
                                      : row.group.id;
                              if (!groupId) return '';
                              return getGroupLabelForLevel(
                                  groupId,
                                  parentLeafLevel?.id,
                              );
                          },
                      },
                  ]),
            {
                header: leafLevel?.name || t('users.leafGroups.title'),
                sortKey: 'leafGroup',
                sortValue: (row: UsersTableRow) => {
                    const groupId =
                        row.type === 'consumer'
                            ? row.consumer.groupIds?.[0]
                            : row.group.id;
                    if (!groupId) return '';
                    return getGroupLabelForLevel(groupId, leafLevel?.id);
                },
            },
            {
                header: t('modules.users.mainContacts'),
                sortKey: 'mainContacts',
                sortValue: (row: UsersTableRow) =>
                    row.type === 'consumer'
                        ? mainContactsByConsumerId
                              .get(row.consumer.id)
                              ?.map(contact => contact.name)
                              .join(', ') || ''
                        : '',
            },
            ...(canCreate ? [{ header: '', className: 'w-0 text-right' }] : []),
        ],
        [
            t,
            mainContactsByConsumerId,
            parentLeafLevel?.name,
            parentLeafLevel?.id,
            leafLevel?.name,
            leafLevel?.id,
            getGroupLabelForLevel,
            canCreate,
        ],
    );

    useEffect(() => {
        dispatch({ type: 'RESET' });
        (async (): Promise<void> => {
            const licence = await munduClient.getNumberOfLicences();
            setLicence(licence);
        })();
    }, [dispatch]);

    useEffect(() => {
        if (!licence) return;
        const isReached = licence.total === licence.active;
        setIsReached(isReached);
    }, [licence]);

    useEffect(() => {
        if (isError) {
            notify(isError.message, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    const onClick = (consumer: Consumer) => {
        return dispatch({
            type: 'SELECT_CONSUMER',
            payload: consumer,
        });
    };

    return (
        <>
            {isShowModalReached && (
                <ModalLimitReached
                    onCancel={(): void => {
                        setIsShowModalReached(false);
                    }}
                />
            )}
            <div className="h-full w-full relative  text-woodyBrown">
                <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
                    {canFilterResidents && (
                        <AccountStatusFilterTabs
                            residentMainContacts
                            value={statusFilter}
                            onChange={setStatusFilter}
                        />
                    )}
                    {rows.length > 10 ? (
                        <Input
                            className="text-input w-full"
                            containerClassName="w-full max-w-xs"
                            placeholder={t('common.search')}
                            leadingIcon={SearchIcon}
                            value={searchValue}
                            onChange={event =>
                                setSearchValue(event.target.value)
                            }
                        />
                    ) : (
                        <div />
                    )}

                    {canCreate && (
                        <Link
                            to={isReached ? '#' : '/app/users/new'}
                            className="primary-btn-small ml-auto flex items-center gap-1 pl-2 pr-4 py-1"
                            onClick={event => {
                                if (isReached) {
                                    event.preventDefault();
                                    setIsShowModalReached(true);
                                }
                            }}
                        >
                            <TextIcon SVG={PlusIcon} />
                            <span>{t('modules.users.navNew')}</span>
                        </Link>
                    )}
                </div>
                <GroupFilterControls
                    levels={filterLevels}
                    selectedFilterCount={selectedFilterCount}
                    onChange={setSelectedIdsForLevel}
                />
                <Table
                    columns={columns}
                    data={filteredRows}
                    sortQueryParamKey="sort"
                    highlight={
                        shouldHighlightUsersWithoutNextOfKin
                            ? {
                                  predicate: (row: UsersTableRow) =>
                                      row.type === 'consumer' &&
                                      !consumerIdsWithNextOfKin.has(
                                          row.consumer.id,
                                      ),
                              }
                            : undefined
                    }
                    renderRow={row => {
                        const parentLevelLabel = (() => {
                            const groupId =
                                row.type === 'consumer'
                                    ? row.consumer.groupIds?.[0]
                                    : row.group.id;
                            if (!groupId) return '-';
                            return (
                                getGroupLabelForLevel(
                                    groupId,
                                    parentLeafLevel?.id,
                                ) || '-'
                            );
                        })();

                        const leafLevelLabel = (() => {
                            const groupId =
                                row.type === 'consumer'
                                    ? row.consumer.groupIds?.[0]
                                    : row.group.id;
                            if (!groupId) return '-';
                            return (
                                getGroupLabelForLevel(groupId, leafLevel?.id) ||
                                '-'
                            );
                        })();

                        if (row.type === 'leafGroup') {
                            return (
                                <tr key={`leaf-${row.group.id}`}>
                                    <td className="px-4 py-3 text-sm text-mongooseGray">
                                        -
                                    </td>
                                    {!isMobile && (
                                        <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                            {parentLevelLabel}
                                        </td>
                                    )}
                                    <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                        {leafLevelLabel}
                                    </td>
                                    <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                        -
                                    </td>
                                    {canCreate && (
                                        <td className="px-4 py-3 content-center text-right" />
                                    )}
                                </tr>
                            );
                        }

                        const { id, profile } = row.consumer;
                        const residentUnreadCount =
                            residentUnreadCounts[id] ??
                            createEmptyUnreadBucket();
                        const showUnreadBadge =
                            residentUnreadCount.hasUnread ||
                            residentUnreadCount.count > 0;

                        const mainContacts =
                            mainContactsByConsumerId.get(id) ?? [];

                        const profileLink = {
                            pathname: `/app/users/profile/${id}`,
                            search: '?tab=profile',
                        };

                        return (
                            <tr key={id}>
                                <td className="px-4 py-3">
                                    <div className="group flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3">
                                        <img
                                            className="h-10 w-10 rounded-full object-cover"
                                            alt={`${profile.firstName} ${profile.lastName}`}
                                            {...getAvatar(profile.avatar)}
                                        />
                                        <Link
                                            to={profileLink.pathname}
                                            className="font-semibold hover:underline whitespace-nowrap"
                                            onClick={() =>
                                                onClick(row.consumer)
                                            }
                                        >
                                            {`${profile.firstName} ${profile.lastName}`}
                                        </Link>
                                        {showUnreadBadge && (
                                            <div className="inline-flex items-center gap-1">
                                                <AttentionBadge
                                                    backgroundClassName="bg-[#FF7979] text-white"
                                                    count={
                                                        residentUnreadCount.count
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </td>
                                {!isMobile && (
                                    <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                        {parentLevelLabel}
                                    </td>
                                )}
                                <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                    {leafLevelLabel}
                                </td>
                                <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                    {mainContacts.length ? (
                                        <div className="flex flex-col gap-1.5">
                                            {mainContacts.map(contact => (
                                                <span
                                                    key={contact.id}
                                                    className="inline-flex items-center gap-1.5"
                                                >
                                                    <LoginStatusIndicator
                                                        hasEverLoggedIn={
                                                            contact.hasEverLoggedIn
                                                        }
                                                        lastLoggedInAt={
                                                            contact.lastLoggedInAt
                                                        }
                                                    />
                                                    <span>{contact.name}</span>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                {canCreate && (
                                    <td className="px-4 py-3 content-center text-right">
                                        <Link
                                            to={profileLink}
                                            className="inline-block primary-btn-small p-1.5"
                                            onClick={() =>
                                                onClick(row.consumer)
                                            }
                                        >
                                            <div className="relative w-4 h-4">
                                                <DocumentsIcon />
                                            </div>
                                        </Link>
                                    </td>
                                )}
                            </tr>
                        );
                    }}
                    emptyMessage={
                        statusFilter !== 'all'
                            ? t(
                                  `modules.users.mainContactFilters.empty.${statusFilter}`,
                              )
                            : searchValue.trim() || selectedFilterCount > 0
                            ? t('modules.users.mainContactFilters.noMatches')
                            : t('modules.users.empty')
                    }
                    isLoading={isLoading}
                    loadingRowCount={3}
                />
            </div>
        </>
    );
};
